import { useEffect, useState } from "react";
import { dashboardService } from "../../services/api";
import StatCard from "../../components/StatCard/StatCard";
import { fmtDateTimezone } from "../utils/dateUtils/fmtDate";
import { fmtCurrencyBR } from "../../utils/formatBrl";
import { CollapsibleMetrics } from '../../components/MetricsToggle/MetricsToggle';

// Valor na moeda da venda; se estrangeira, mostra também o equivalente em BRL.
function PaymentAmount({ p }) {
	const cur = (p.currency || "BRL").toUpperCase();
	if (cur === "BRL") return <>{fmtCurrencyBR.format(p.amount)}</>;
	const native = new Intl.NumberFormat("pt-BR", { style: "currency", currency: cur }).format(p.amount || 0);
	return (
		<>
			{native}
			<span style={{ color: "var(--text-secondary)", marginLeft: 6, fontSize: "0.85em" }}>
				≈ {fmtCurrencyBR.format(p.amount_brl ?? p.amount)}
			</span>
		</>
	);
}

export default function AdminPayments() {
	const [payments, setPayments] = useState([]);
	const [loading, setLoading] = useState(false);
	const [filters, setFilters] = useState({
		username: "",
		email: "",
		start_date: "",
		end_date: "",
	});
	const [metrics, setMetrics] = useState({
		payments_revenue: 0,
		payments_generated_sum: 0,
		payments_paid_sum: 0,
		payments_approved_count: 0,
		payments_pending_count: 0,
	});
	const [pagination, setPagination] = useState({});
	const [currentPage, setCurrentPage] = useState(1);

	useEffect(() => {
		loadPayments(1);
		// eslint-disable-next-line
	}, []);

	const loadPayments = async (page = 1) => {
		try {
			setLoading(true);
			const { data } = await dashboardService.getAdminPayments({
				page,
				...filters,
			});
			setPayments(data.payments || []);
			setMetrics({
				payments_revenue: data.payments_revenue,
				payments_generated_sum: data.payments_generated_sum,
				payments_paid_sum: data.payments_paid_sum,
				payments_approved_count: data.payments_approved_count,
				payments_pending_count: data.payments_pending_count,
			});
			setPagination(data.pagination || {});
			setCurrentPage(page);
		} catch (err) {
			console.error(err);
		} finally {
			setLoading(false);
		}
	};

	const handleFilterChange = (e) => {
		setFilters((prev) => ({
			...prev,
			[e.target.name]: e.target.value,
		}));
	};

	const applyFilters = () => {
		loadPayments(1);
	};

	const formatStatusLabel = (status) => {
		if (!status) return "-";
		const normalized = status.toLowerCase();
		if (normalized === "approved") return "Pago";
		if (normalized === "pending") return "Pendente";
		if (normalized === "expired") return "Expirado";
		if (normalized === "failed") return "Falhou";
		return status;
	};

	const getStatusClass = (status) => {
		const normalized = (status || "").toLowerCase();
		if (normalized === "approved") return "text-green-400";
		if (normalized === "pending") return "text-yellow-400";
		if (normalized === "expired") return "text-gray-400";
		if (normalized === "failed") return "text-red-400";
		return "text-gray-300";
	};

	const getBgClass = (status) => {
		const normalized = (status || "").toLowerCase();
		if (normalized === "approved") return "bg-green-900";
		if (normalized === "pending") return "bg-yellow-900";
		if (normalized === "expired") return "bg-gray-900";
		if (normalized === "failed") return "bg-red-900";
		return "bg-gray-300";
	};

	return (
		<>
			{/* FILTROS */}
			<div className="filters-section">
				<div className="filters-grid">
					<div className="filter-group">
						<input
							type="date"
							name="start_date"
							value={filters.start_date}
							onChange={handleFilterChange}
							className="filter-input"
						/>
					</div>
					<div className="filter-group">
						<input
							type="date"
							name="end_date"
							value={filters.end_date}
							onChange={handleFilterChange}
							className="filter-input"
						/>
					</div>
					<div className="filter-group">
						<input
							type="text"
							name="username"
							placeholder="Username"
							value={filters.username}
							onChange={handleFilterChange}
							className="filter-input"
						/>
					</div>
					<div className="filter-group">
						<input
							type="text"
							name="email"
							placeholder="Email"
							value={filters.email}
							onChange={handleFilterChange}
							className="filter-input"
						/>
					</div>
					<button onClick={applyFilters} className="btn-primary">
						Aplicar Filtros
					</button>
				</div>
			</div>

			{/* CARDS RESUMO */}
			<CollapsibleMetrics>
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
					<StatCard title="Faturamento" value={fmtCurrencyBR.format(metrics.payments_revenue)} icon={{ class: 'fas fa-chart-line', color: 'green' }} />
					{/* "Comissão": soma do split (taxa da Spectrum), não o valor das vendas. */}
					<StatCard title="Comissão gerada" value={fmtCurrencyBR.format(metrics.payments_generated_sum)} icon={{ class: 'fas fa-file-invoice-dollar', color: 'blue' }} />
					<StatCard title="Comissão paga" value={fmtCurrencyBR.format(metrics.payments_paid_sum)} icon={{ class: 'fas fa-money-bill', color: 'green' }} />
					<StatCard title="Pagamentos Aprovados" value={metrics.payments_approved_count} icon={{ class: 'fas fa-check-circle', color: 'purple' }} />
					<StatCard title="Pagamentos Pendentes" value={metrics.payments_pending_count} icon={{ class: 'fas fa-clock', color: 'red' }} />
				</div>
			</CollapsibleMetrics>

			{/* TABELA - Desktop */}
			<div className="sx-desktop mt-6">
				<table className="sx-table">
					<thead>
						<tr>
							<th>ID</th>
							<th>SELLER</th>
							<th>Valor</th>
                            <th>Split</th>
							<th>Status</th>
							<th>Data</th>
						</tr>
					</thead>
					<tbody>
						{payments.map((p) => (
							<tr key={p.id}>
								<td>{p.id}</td>
								<td>{p.user_id || '-'} - {p.username}</td>
								<td><PaymentAmount p={p} /></td>
								<td>{fmtCurrencyBR.format(p.split)}</td>
								<td className={getStatusClass(p.status)}>
									<div className={getBgClass(p.status) + " font-bold inline-block px-2 py-1 rounded text-xs font-semibold"}>
										{formatStatusLabel(p.status)}
									</div>
								</td>
								<td>{fmtDateTimezone(p.created_at)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* CARDS - Mobile */}
			<div className="sx-mobile mt-6">
				{payments.map((p) => (
					<div key={p.id} className="mobile-card">
						<div className="sx-card-header">
							<span className="sx-card-title">{p.username}</span>
							<span className={getBgClass(p.status) + " font-bold inline-block px-2 py-1 rounded text-xs font-semibold " + getStatusClass(p.status)}>
								{formatStatusLabel(p.status)}
							</span>
						</div>
						<div className="sx-card-row">
							<span style={{ color: "var(--text-secondary)" }}>ID: </span>
							{p.id} · User {p.user_id || '-'}
						</div>
						<div className="sx-card-row">
							<span style={{ color: "var(--text-secondary)" }}>Valor: </span>
							<strong><PaymentAmount p={p} /></strong>
							<span style={{ color: "var(--text-secondary)", marginLeft: 8 }}>Split: </span>
							<strong>{fmtCurrencyBR.format(p.split)}</strong>
						</div>
						<div className="sx-card-row">
							<span style={{ color: "var(--text-secondary)" }}>Data: </span>
							{fmtDateTimezone(p.created_at)}
						</div>
					</div>
				))}
				{payments.length === 0 && (
					<div className="empty-state">
						<i className="fas fa-money-bill" />
						<h4>Nenhum pagamento encontrado</h4>
					</div>
				)}
			</div>

			{/* PAGINAÇÃO */}
			{!loading && payments.length > 0 && (
				<div className="mt-8 flex items-center justify-between">
					<div className="text-sm text-gray-600">
						Página {currentPage} de {pagination.pages} (
						{pagination.total} pagamentos)
					</div>
					<div className="flex gap-2">
						<button
							onClick={() => loadPayments(currentPage - 1)}
							disabled={!pagination.has_prev}
							className="btn-secondary"
						>
							<i className="fas fa-chevron-left"></i> Anterior
						</button>
						<button
							onClick={() => loadPayments(currentPage + 1)}
							disabled={!pagination.has_next}
							className="btn-primary"
						>
							Próximo <i className="fas fa-chevron-right"></i>
						</button>
					</div>
				</div>
			)}
		</>
	);
}
